if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileMql = window.matchMedia('(max-width: 749px)');
        this.visibleSlides = new Map();

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 150));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        this.initMobileVideoAutoplay();
      }

      initMobileVideoAutoplay() {
        const setup = () => {
          this.disconnectVideoObserver();
          if (!this.mobileMql.matches || !this.elements.viewer) return;

          const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
          const slides = this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]');
          if (!slides.length) return;

          this.visibleSlides = new Map();
          this.videoObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.visibleSlides.set(entry.target, entry.intersectionRatio);
                } else {
                  this.visibleSlides.delete(entry.target);
                }
              });

              let bestSlide = null;
              let bestRatio = 0;
              this.visibleSlides.forEach((ratio, slide) => {
                if (ratio > bestRatio) {
                  bestRatio = ratio;
                  bestSlide = slide;
                }
              });

              if (bestSlide && bestRatio >= 0.5) {
                this.setActiveSlide(bestSlide, { autoplay: true });
              }
            },
            {
              root: slider,
              threshold: [0.25, 0.5, 0.75, 1],
            }
          );

          slides.forEach((slide) => this.videoObserver.observe(slide));
        };

        setup();
        this.mobileMql.addEventListener('change', setup);
      }

      disconnectVideoObserver() {
        if (!this.videoObserver) return;
        this.videoObserver.disconnect();
        this.videoObserver = null;
        this.visibleSlides = new Map();
      }

      onSlideChanged(event) {
        const activeSlide = event.detail.currentElement;
        this.setActiveSlide(activeSlide, { autoplay: this.mobileMql.matches });
      }

      setActiveSlide(activeSlide, options = {}) {
        if (!activeSlide) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeSlide);
        });

        if (options.autoplay) {
          this.playActiveMedia(activeSlide);
        }

        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeSlide.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (!activeItem) return;

        window.pauseAllMedia();

        const deferredMedia = activeItem.querySelector('deferred-media');
        if (!deferredMedia) return;

        deferredMedia.loadContent(false);
        this.playLoadedVideo(deferredMedia);
      }

      playLoadedVideo(deferredMedia) {
        const tryPlay = () => {
          const video = deferredMedia.querySelector('video');
          if (!video) return;

          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');

          if (this.mobileMql.matches) {
            video.removeAttribute('controls');
            video.controls = false;
            video.classList.add('ssw-gallery-video--chromeless');
          } else {
            video.controls = true;
          }

          const attemptPlay = (muted) => {
            video.muted = muted;
            const playPromise = video.play();
            if (playPromise && playPromise.catch) {
              playPromise.catch(() => {
                if (!muted) attemptPlay(true);
              });
            }
          };

          attemptPlay(this.mobileMql.matches);
        };

        if (deferredMedia.hasAttribute('loaded')) {
          tryPlay();
          return;
        }

        const observer = new MutationObserver(() => {
          if (!deferredMedia.hasAttribute('loaded')) return;
          observer.disconnect();
          tryPlay();
        });

        observer.observe(deferredMedia, { attributes: true, attributeFilter: ['loaded'] });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
